import client from './client';
import { SearchResults } from '../types/index';

// WI #323: this module typed the `/search` response as `SearchResults`, whose
// `users`/`companies`/`projects` arrays were declared as the full camelCase
// `User`, `Company` and `Project` types. Two things were wrong at once:
//
//  * the endpoint does not return those entities — it returns a flat
//    `{id, type, name, ...}` projection per hit; and
//  * what it does return is snake_case, with nothing converting it.
//
// So `results.users[i].displayName` was `undefined`, and the search page —
// which builds each row's name from exactly that field — rendered user hits
// with no name at all. Companies and projects showed a name only because
// `name` happens to be spelled the same on both sides.
//
// Mapped by hand here, per CLAUDE.md's convention 1.
interface RawSearchResult {
  id: string;
  type: string;
  name: string;
  // Users
  email?: string | null;
  department?: string | null;
  // Companies
  domain?: string | null;
  source_system?: string | null;
  // Projects
  description?: string | null;
  client_id?: string | null;
}

interface RawSearchResults {
  users: RawSearchResult[];
  companies: RawSearchResult[];
  projects: RawSearchResult[];
  query: string;
  total: number;
}

const mapSearchResult = (r: RawSearchResult) => ({
  id: r.id,
  type: r.type,
  name: r.name,
  email: r.email ?? undefined,
  department: r.department ?? undefined,
  domain: r.domain ?? undefined,
  sourceSystem: r.source_system ?? undefined,
  description: r.description ?? undefined,
  clientId: r.client_id ?? undefined,
});

export const search = async (query: string): Promise<SearchResults> => {
  const response = await client.get<RawSearchResults>('/search', {
    // `q` is the backend's own spelling — no mapping needed, but anything
    // added here must match what FastAPI binds.
    params: { q: query },
  });
  const data = response.data;
  return {
    users: (data.users || []).map(mapSearchResult),
    companies: (data.companies || []).map(mapSearchResult),
    projects: (data.projects || []).map(mapSearchResult),
    query: data.query,
    total: data.total,
  };
};
